import { FormControlConstants, type FormControlModel, type FormFieldModel, type OasisDefinitionViewModel } from '@generated';
import { hideControlsAndOptions } from '~/libraries/forms/hide';
import { getInitialValues } from '~/libraries/forms/values';

export interface OasisAnswerRow {
	code: string;
	label: string;
	answerHtml: string | null;
}

const { formControlTypes } = FormControlConstants;

const optionControlTypes: string[] = [
	formControlTypes.radioGroup,
	formControlTypes.horizontalRadioGroup,
	formControlTypes.select,
	formControlTypes.checkboxGroup
];

const yesNoControlTypes: string[] = [formControlTypes.checkbox, formControlTypes.checkboxOneOrZero];

const textControlTypes: string[] = [
	formControlTypes.text,
	formControlTypes.textBox,
	formControlTypes.dashTextbox,
	formControlTypes.textBoxCalculatd,
	formControlTypes.datePicker
];

const captionControlTypes: string[] = [formControlTypes.label, formControlTypes.html, formControlTypes.warning];

const notAssessedValues = ['', '-', '^'];

export function stripHtml(html: string | undefined | null): string {
	return (html ?? '')
		.replace(/<[^>]+>/g, '')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/\s+/g, ' ')
		.trim();
}

export function getOasisAnswerRows(definition: OasisDefinitionViewModel, values: Record<string, string>): OasisAnswerRow[] {
	const { documentationFormControls, documentationFormFields } = definition;
	const clientValues = getInitialValues(documentationFormFields, values) ?? {};
	const visibleControls = hideControlsAndOptions(documentationFormControls, documentationFormFields, clientValues);
	const rows: OasisAnswerRow[] = [];
	collectRows(visibleControls, documentationFormFields, values, rows);
	return rows;
}

function collectRows(controls: FormControlModel[], fields: FormFieldModel[], values: Record<string, string>, rows: OasisAnswerRow[]) {
	let precedingCaption: string | null = null;
	for (const control of controls) {
		if (control.hidden) {
			continue;
		}
		if (control.controlType === formControlTypes.container) {
			collectRows(control.formControls ?? [], fields, values, rows);
			precedingCaption = null;
			continue;
		}
		if (captionControlTypes.includes(control.controlType)) {
			precedingCaption = stripHtml(control.label ?? control.description);
			continue;
		}
		const field = control.name ? fields.find(x => x.name === control.name) : undefined;
		if (control.name && !field?.hiddenField && isAnswerControl(control)) {
			rows.push({
				code: control.name,
				label: resolveLabel(control, field, precedingCaption),
				answerHtml: resolveAnswerHtml(control, values[control.name])
			});
		}
		for (const option of control.options ?? []) {
			if (option.formControls) {
				collectRows(option.formControls, fields, values, rows);
			}
		}
		if (control.formControls) {
			collectRows(control.formControls, fields, values, rows);
		}
	}
}

function isAnswerControl(control: FormControlModel) {
	return (
		optionControlTypes.includes(control.controlType) ||
		yesNoControlTypes.includes(control.controlType) ||
		textControlTypes.includes(control.controlType)
	);
}

function resolveLabel(control: FormControlModel, field: FormFieldModel | undefined, precedingCaption: string | null): string {
	const controlLabel = stripHtml(control.label);
	if (controlLabel) {
		return controlLabel;
	}
	if (precedingCaption) {
		return precedingCaption.split(':')[0];
	}
	if (field?.label && field.label !== field.name) {
		return field.label;
	}
	return control.name!;
}

function resolveAnswerHtml(control: FormControlModel, value: string | undefined): string | null {
	if (value === undefined || notAssessedValues.includes(value)) {
		return null;
	}
	if (yesNoControlTypes.includes(control.controlType)) {
		return value === '1' || value === 'true' ? 'Yes' : 'No';
	}
	if (optionControlTypes.includes(control.controlType)) {
		const selected = value.split(',');
		const labels = (control.options ?? []).filter(option => selected.includes(option.value)).map(option => option.label);
		return labels.length > 0 ? labels.join('; ') : value;
	}
	return value;
}
